import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Tab,
  TextField,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import {grey} from '@mui/material/colors'
import Box from '@mui/system/Box'
import AwardsSearchResult from 'app/components/AwardsSearchResult'
import BusinessSearchResult from 'app/components/BusinessSearchResult'
import ConceptsSearchResult from 'app/components/ConceptsSearchResult'
import InnovationsSearchResult from 'app/components/InnovationsSearchResult'
import OpportunitySearchResult from 'app/components/OpportunitySearchResult'
import PersonSearchResult from 'app/components/PersonSearchResult'
import {
  CFilters,
  IFilterControlOptions,
  IStateFilter,
  formatFiltersDisplay,
  formatFiltersFetch,
  formatFuzzyOrder,
  saveFiltersCF,
} from 'app/components/filters/CFilters'
import FilterListPills from 'app/components/filters/FilterListPills'
import UniversalFilter from 'app/components/filters/UniversalFilter'
import {PaginationSettings} from 'app/components/pagination/CustomIconPagination'
import {useGetEntityByIdsV2} from 'app/hooks/entities/useGetEntityByIdsV2'
import {useGetEntitySearchV2} from 'app/hooks/entities/useGetEntitySearchV2'
import {useGetUniversities} from 'app/hooks/entities/useGetUniversities'
import {useAuthProvider} from 'app/modules/auth'
import {IBusiness} from 'app/modules/discovery/core'
import {debounce} from 'lodash'
import React, {useState} from 'react'
import CardHeader from 'react-bootstrap/esm/CardHeader'
import {useDispatch} from 'react-redux'
import {useSearchParams} from 'react-router-dom'
import {AppDispatch} from 'redux/store'

function getEntityDisplayName(entityName: string, plural = false, capitalize = false) {
  switch (entityName) {
    case 'business': {
      let retval = 'Business'
      if (plural) {
        if (!capitalize) {
          retval = 'businesses'
        } else {
          retval = 'Businesses'
        }
      }
      return retval
    }
    case 'award': {
      let retval = 'Award'
      if (plural) {
        if (!capitalize) {
          retval = 'awards'
        } else {
          retval = 'Award'
        }
      }
      return retval
    }
    case 'innovation': {
      let retval = 'Innovation'
      if (plural) {
        if (!capitalize) {
          retval = 'innovations'
        } else {
          retval = 'Innovation'
        }
      }
      return retval
    }
    case 'concept': {
      let retval = 'Concept'
      if (plural) {
        if (!capitalize) {
          retval = 'concepts'
        } else {
          retval = 'Concepts'
        }
      }
      return retval
    }
    case 'person': {
      let retval = 'Person'
      if (plural) {
        if (!capitalize) {
          retval = 'people'
        } else {
          retval = 'People'
        }
      }
      return retval
    }
    case 'opportunity': {
      let retval = 'Opportunity'
      if (plural) {
        if (!capitalize) {
          retval = 'opportunity'
        } else {
          retval = 'opportunity'
        }
      }
      return retval
    }
    default: {
      return ''
    }
  }
}

interface DirectorySearchListContextState {
  page: number
  setPage: (page: number) => void
}

const initialState: DirectorySearchListContextState = {
  page: PaginationSettings.page,
  setPage: (page: number) => {},
}
const DirectorySearchListContext =
  React.createContext<DirectorySearchListContextState>(initialState)

function DirectoriesSearchListProvider({children}: {children: React.ReactNode}) {
  const [page, setPage] = React.useState<number>(initialState.page)

  return (
    <DirectorySearchListContext.Provider value={{page, setPage}}>
      {children}
    </DirectorySearchListContext.Provider>
  )
}
export function useDirectoriesSearchListContext(): DirectorySearchListContextState {
  return React.useContext(DirectorySearchListContext)
}

//businesses/all route component
export default function DirectoriesSearchList({
  entity_type,
  classType,
}: {
  entity_type: string
  classType: any
}): React.ReactElement {
  // const {page, setPage} = useDirectoriesSearchListContext()
  const dispatch = useDispatch<AppDispatch>()
  const [open, setOpen] = useState<boolean>(false)
  const [searchParams] = useSearchParams()
  const {reduxUser} = useAuthProvider()
  const pageParam = searchParams.get('page')
  const searchEntered = searchParams.get('search')
  const [scrollPosition, setScrollPosition] = React.useState<number>(
    Number(searchParams.get('scrollPosition') ?? 0)
  )

  const [activeTab, setActiveTab] = React.useState<string>(entity_type)
  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue)
    setPage(1)
    setScrollPosition(0)
  }

  const [page, setPage] = React.useState<number>(
    pageParam ? Number.parseInt(pageParam) : PaginationSettings.page
  )

  const profileFilters = reduxUser?.profile?.filters?.map((f) => {
    const x = formatFiltersDisplay(f.filters)
    const obj = {type: f.type, filters: x}
    return obj
  })
  //set the filters for the chosen entity type
  const [stateFilters, setStateFilters] = React.useState<
    {type: string; filters: Record<string, IStateFilter>}[]
  >(
    Array.isArray(profileFilters)
      ? profileFilters
      : [
          {
            type: '',
            filters: {none: {value: '', display: ''}},
          },
        ]
  )

  const [itemsPP, setItemsPP] = React.useState<number>(PaginationSettings.itemsPPDirectory)

  const [prevPage, setPrevPage] = React.useState<number>(
    pageParam ? Number.parseInt(pageParam) - 1 : PaginationSettings.page
  )
  const [searchVal, setSearchVal] = React.useState<string>(searchEntered ?? '')
  const [searchDebouncedVal, setSearchDebouncedVal] = React.useState<string>(searchEntered ?? '')
  const debouncedSearchVal = React.useCallback(
    debounce((newValue) => setSearchDebouncedVal(newValue), 800),
    []
  )
  const updateSearch = (newValue: string) => {
    //sets the value for the user instantly
    setSearchVal(newValue)
    //awaits with debounce before running new fetch
    debouncedSearchVal(newValue)
    //search so send back to page 1
    setPage(PaginationSettings.page)
    setPrevPage(PaginationSettings.prev)
    setScrollPosition(0)
  }
  //pagination
  const handlePageChange = (e: React.ChangeEvent<unknown>, v: number) => {
    setPage(v)
    setPrevPage(v - 1)
    setScrollPosition(0)
  }
  //these need defind based on entity type
  const favorites =
    (reduxUser?.profile?.favorites as any)?.[getEntityDisplayName(activeTab, true, false)] ?? []

  const recents =
    (reduxUser?.profile?.recent_activity as any)?.[getEntityDisplayName(activeTab, true, false)] ??
    []

  const combined = [...favorites, ...recents]

  const [stateFiltersOptions, setStateFiltersOptions] = React.useState<IFilterControlOptions[]>(
    reduxUser?.profile?.filters?.find((f) => f.type === activeTab)?.filters ?? []
  )

  const saveFilters = async (filters: IFilterControlOptions[], filterType: string) => {
    reduxUser &&
      saveFiltersCF(
        filters,
        filterType,
        reduxUser,
        setStateFilters,
        dispatch,
        setStateFiltersOptions
      )
  }

  //needs defined based on entity type

  const entities: any =
    combined.length > 0 &&
    !searchDebouncedVal &&
    !Object.entries(
      formatFiltersDisplay(
        reduxUser?.profile?.filters?.find((f) => f.type === activeTab)?.filters
      ) ?? {}
    ).length
      ? useGetEntityByIdsV2<typeof classType>(`${activeTab}`, combined, page, itemsPP)
      : useGetEntitySearchV2<typeof classType>({
          entity: activeTab,
          page: page,
          items_per_page: itemsPP,
          filters: formatFiltersFetch(
            Array.isArray(stateFilters)
              ? stateFilters?.find((f) => f.type === activeTab)?.filters
              : undefined
          ),
          fuzzyOrder: formatFuzzyOrder(stateFiltersOptions),
          search_str: searchDebouncedVal,
          with_count: true,
        })

  //Need url param scrollPosition sent from business overview if we need this later
  React.useEffect(() => {
    if (entities.data && !entities.isLoading && scrollPosition && scrollPosition !== 0) {
      window.scrollTo(0, scrollPosition)
    }
  }, [scrollPosition, entities])

  //filter class gets defined options for the entity, takes a node that it will return with the defined filters,
  //the node takes the filters as props and saves values to profile. The node also tkes a callbak that returns
  //the filters in an object array to be used here
  //** since the filterModule has stateFilters for viewing and we've also saved to the profile, applyFilters is the only method that acutally "SAVES"
  // the filters permanetaly, otherwise, deleteing a pill/chip oly removes from state, but resetting and applying actually save*/

  const universities = useGetUniversities()

  const filterModule = new CFilters(
    {
      entity_type: activeTab,
      additional_data:
        activeTab === 'innovation'
          ? {
              forType: 'multichip',
              values: universities?.data?.records?.map((u: IBusiness) => {
                return {key: u.id, value: u.name}
              }),
            }
          : undefined,
    },
    {
      node: (
        <UniversalFilter
          entity={activeTab}
          title={`${getEntityDisplayName(activeTab)} Search Directory Filters`}
          parentSaveFilters={saveFilters}
          parentResetFilters={saveFilters}
          anchor={'right'}
          currUser={reduxUser}
        />
      ),
    }
  )

  return (
    <DirectoriesSearchListProvider>
      {filterModule?._filterComponent}
      <Card elevation={0} sx={{m: 3}}>
        <CardHeader>
          <TabContext value={activeTab}>
            <Box sx={{borderBottom: 1, borderColor: '#eeeeee', width: '92%'}}>
              <TabList onChange={handleChangeTab} textColor='inherit'>
                <Tab label='Businesses' value={'business'} />
                <Tab label='Awards' value={'award'} />
                <Tab label='Concepts' value={'concept'} />
                <Tab label='People' value={'person'} />
                <Tab label='Innovations' value={'innovation'} />
                <Tab label='Opportunities' value={'opportunity'} />
              </TabList>
            </Box>
          </TabContext>
        </CardHeader>

        <CardContent>
          <Typography variant={'h5'} ml={2} mb={1}>{`${getEntityDisplayName(
            activeTab
          )} Directory`}</Typography>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Box display={'flex'} ml={2}>
              <TextField
                value={searchVal}
                sx={{minWidth: 250}}
                label={`Search for ${activeTab}`}
                onChange={(event) => {
                  updateSearch(event.target.value)
                }}
              />
              {(Object.keys(
                formatFiltersDisplay(
                  reduxUser?.profile?.filters?.find((f) => f.type === activeTab)?.filters ?? []
                )
              )?.length ?? 0) > 0 && (
                <Box ml={4}>
                  <Typography color={grey[600]}>Current Filters Set:</Typography>
                  <FilterListPills
                    filterList={formatFiltersDisplay(
                      reduxUser?.profile?.filters?.find((f) => f.type === activeTab)?.filters ?? []
                    )}
                    handleStateAction={setStateFilters}
                    handleDelete={filterModule._filterComponent.props.handleDelete}
                    universities={universities?.data?.records?.map((u: IBusiness) => {
                      return {key: u.id, value: u.name}
                    })}
                  />
                </Box>
              )}
            </Box>
            {activeTab === 'innovation' && (
              <Box mr={5}>
                <Button variant={'contained'} onClick={() => setOpen(true)} color={'primary'}>
                  Directory
                </Button>
              </Box>
            )}
          </Box>
          <TabContext value={activeTab}>
            <TabPanel value={'business'} sx={{padding: 0}}>
              {' '}
              <BusinessSearchResult
                results={entities.data?.records}
                loading={entities?.isLoading}
                page={page}
                handlePagination={handlePageChange}
                itemsPP={itemsPP}
                totalCount={entities.data?.count}
                search={searchDebouncedVal}
                prev={entities.data?.prev}
                next={entities.data?.next}
              />
            </TabPanel>
            <TabPanel value={'award'} sx={{padding: 0}}>
              {' '}
              <AwardsSearchResult
                results={entities.data?.records}
                loading={entities?.isLoading}
                page={page}
                handlePagination={handlePageChange}
                itemsPP={itemsPP}
                totalCount={entities.data?.count}
                search={searchDebouncedVal}
                prev={entities.data?.prev}
                next={entities.data?.next}
              />
            </TabPanel>
            <TabPanel value={'innovation'} sx={{padding: 0}}>
              {' '}
              <InnovationsSearchResult
                results={entities.data?.records}
                loading={entities?.isLoading}
                page={page}
                handlePagination={handlePageChange}
                itemsPP={itemsPP}
                totalCount={entities.data?.count}
                search={searchDebouncedVal}
                prev={entities.data?.prev}
                next={entities.data?.next}
              />{' '}
            </TabPanel>
            <TabPanel value={'concept'} sx={{padding: 0}}>
              {' '}
              <ConceptsSearchResult
                results={entities.data?.records}
                loading={entities?.isLoading}
                page={page}
                handlePagination={handlePageChange}
                itemsPP={itemsPP}
                totalCount={entities.data?.count}
                search={searchDebouncedVal}
                prev={entities.data?.prev}
                next={entities.data?.next}
              />
            </TabPanel>
            <TabPanel value={'person'} sx={{padding: 0}}>
              {' '}
              <PersonSearchResult
                results={entities.data?.records}
                loading={entities?.isLoading || entities?.isFetching}
                page={page}
                handlePagination={handlePageChange}
                itemsPP={itemsPP}
                totalCount={entities.data?.count}
                search={searchDebouncedVal}
                scrollPosition={scrollPosition}
                prev={entities.data?.prev}
                next={entities.data?.next}
              />
            </TabPanel>
            <TabPanel value={'opportunity'} sx={{padding: 0}}>
              {' '}
              <OpportunitySearchResult
                results={entities.data?.records}
                loading={entities?.isLoading || entities?.isFetching}
                page={page}
                handlePagination={handlePageChange}
                itemsPP={itemsPP}
                totalCount={entities.data?.count}
                search={searchDebouncedVal}
                scrollPosition={scrollPosition}
                prev={entities.data?.prev}
                next={entities.data?.next}
              />
            </TabPanel>
          </TabContext>
        </CardContent>
      </Card>
      <Dialog
        maxWidth={'lg'}
        fullWidth
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
        scroll={'paper'}
      >
        <Divider />
        <DialogContent sx={{width: '100%'}}>
          <BusinessSearchResult
            results={universities.data?.records}
            loading={universities?.isLoading}
            page={page}
            handlePagination={handlePageChange}
            itemsPP={itemsPP}
            totalCount={universities.data?.count}
            search={searchDebouncedVal}
            prev={universities.data?.prev}
            next={universities.data?.next}
            showSmaller={true}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </DirectoriesSearchListProvider>
  )
}
